import { useEffect, useMemo, useRef, useState } from 'react';
import { createStore, useStore } from '../app/store';
import { type Item } from '../app/menu';
import { bulkMenu, courierMenu, groupItems, planMenu, routeMenu, shareMenu, stopMenu, waitingBulkMenu, waitingMenu, zoneMenu } from '../app/menus';
import { track } from '../data/analytics';
import { pastStops } from '../data/routesPage';
import { openDialog } from '../app/dialogs';
import { besideMap, drawZone, go, importSheet, onMapPage, openPerson, openRoute, openShare, openStop, openWaiting, openZones, planWith, setMap, theme, ui, type Page } from '../app/ui';
import { downloadReport } from '../pages/Reports';
import { waitingRows } from '../pages/Waiting';
import { keyOf, routeTitle, type Model } from '../data/model';
import { routeOfPerson } from '../pages/Live';
import { courierState } from '../pages/Couriers';
import { dayWords, streetOf, plural, and, phoneMatches } from '../app/words';
import { Av, EmptyHelp, nearly } from './bits';
import { Icon } from './Icon';

/**
 * ⌘K (control-room-web.html, "The command bar"): the actions of what's open first, then couriers, stops, pages and the
 * rest. The same actions as the menus, found by typing.
 */
export const cmdk = createStore<{ open: boolean }>({ open: false });
export const toggleCmdk = (open = !cmdk.get().open) => cmdk.set({ open });

interface Hit {
  icon?: string;
  avatar?: Item['avatar'];
  label: string;
  note?: string;
  key?: string;
  run: () => void;
}

/** What's open, and its actions. */
function contextOf(m: Model): [string | null, Item[]] {
  const s = ui.get();
  if (besideMap(s) && s.view === 'stop' && s.stop) {
    const st = m.stop(s.stop);
    if (st) return [streetOf(st), stopMenu(s.stop)];
  }
  if (s.rec?.type === 'stop') {
    const st = m.stop(s.rec.id);
    if (st) return [streetOf(st), stopMenu(s.rec.id)];
  }
  if (s.page === 'live' && s.view === 'share' && s.share) return [`Sharing out ${plural(s.share.ids.length, 'stop')}`, shareMenu()];
  if (s.page === 'live' && s.view === 'plan' && s.plan) return [`the new route · ${plural(s.plan.stops.length, 'stop')}`, planMenu()];
  if (s.page === 'couriers' && s.ctab === 'zones' && s.zone) {
    const z = m.zones.find((x) => x.id === s.zone);
    if (z) return [z.name, zoneMenu(z.id)];
  }
  if (s.page === 'stops' && s.tab === 'wait') {
    const picked = [...s.sel].filter((id) => m.waiting.some((w) => w.id === id));
    if (picked.length > 1) return [`${plural(picked.length, 'waiting stop')} selected`, waitingBulkMenu(picked)];
    const w = m.waiting.find((x) => x.id === (picked[0] ?? (s.rec?.type === 'waiting' ? s.rec.id : '')));
    if (w) return [w.street, waitingMenu(w.id)];
  }
  const sel = [...s.sel].filter((k) => m.stop(k));
  if (sel.length > 1) return [`${plural(sel.length, 'stop')} selected`, bulkMenu(sel)];
  if (onMapPage(s.page) && (s.view === 'route' || s.view === 'stop') && s.route) {
    const r = m.route(s.route);
    if (r) return [`${m.person(r.riderId)?.first ?? 'A courier'} · ${routeTitle(r, m.zone)}`, routeMenu(s.route)];
  }
  if (s.rec?.type === 'courier') {
    const p = m.person(s.rec.id);
    if (p) return [p.name, courierMenu(s.rec.id)];
  }
  if (onMapPage(s.page) && s.view === 'person' && s.person) {
    const p = m.person(s.person);
    if (p) return [p.name, courierMenu(s.person)];
  }
  if (s.page === 'reports') return ['Analytics', [{ label: `Download these ${s.range} days as a CSV`, icon: 'downloadSimple', off: m.history == null, run: () => downloadReport(m, s.range) }]];
  if (s.page === 'stops' && s.tab !== 'wait') return ['the stops', groupItems().map((x) => ({ ...x, label: `Group stops: ${x.label?.toLowerCase()}` }))];
  return [null, []];
}

/** Menu items as ⌘K rows: a submenu's choices each become a row of their own. */
const flat = (items: Item[]): Hit[] =>
  items.flatMap((it) =>
    it.sub && !it.off
      ? it
          .sub()
          .filter((x) => !x.off && x.run)
          .map((x) => ({ icon: x.icon ?? it.icon, avatar: x.avatar, label: `${it.label} ${x.label}${x.value ? ` · ${x.value}` : ''}`, run: x.run! }))
      : it.label && !it.off && it.run
        ? [{ icon: it.icon, avatar: it.avatar, label: it.label, key: it.key, run: it.run }]
        : [],
  );

const PAGES: [Page, string, string][] = [
  ['live', 'mapTrifold', 'Live'],
  ['stops', 'mapPin', 'Stops'],
  ['routes', 'path', 'Routes'],
  ['couriers', 'users', 'Couriers'],
  ['reports', 'chartBar', 'Analytics'],
];

export function CommandBar({ m }: { m: Model }) {
  const { open } = useStore(cmdk);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const res = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) {
      setQ('');
      setSel(0);
      setTimeout(() => input.current?.focus(), 10);
    }
  }, [open]);
  const groups = useMemo(() => {
    if (!open) return [] as [string, Hit[]][];
    const ql = q.trim().toLowerCase();
    const [thing, items] = contextOf(m);
    const country = m.business?.country ?? null;
    const has = (t: string) => !ql || t.toLowerCase().includes(ql);
    const stops = ql
      ? m.stops
          .filter((s) => [s.address, s.name, s.area, s.phone, s.trackingCode, m.person(s.riderId)?.first].join(' ').toLowerCase().includes(ql) || phoneMatches(q, s.phone, country))
          .slice(0, 6)
          .map((s) => ({ icon: 'mapPin', label: streetOf(s), note: [s.name, m.person(s.riderId)?.first].filter(Boolean).join(' · '), run: () => openStop(keyOf(s)) }))
      : [];
    // Today's stops first; past days only when those don't fill the list.
    const past = stops.length < 6 ? pastStops(m.history, q, m.today, m.zone) : { hits: [], total: 0 };
    const out: [string, Hit[]][] = [
      [thing ? `For ${thing}` : '', flat(items).filter((h) => has(h.label))],
      ['Couriers', m.people.filter((p) => has(`${p.name} ${p.courier.phone ?? ''}`) || phoneMatches(q, p.courier.phone, country)).map((p) => ({ avatar: { hex: p.hex, on: p.on, first: p.first, photo: p.courier.photoUrl }, label: p.name, note: courierState(m, p).text, run: () => openPerson(p.uid, routeOfPerson(m)) }))],
      ['Stops', stops],
      [
        'Waiting for a route',
        ql
          ? m.waiting
              .filter((w) => [w.address, w.name, w.phone, w.notes].join(' ').toLowerCase().includes(ql) || phoneMatches(q, w.phone, country))
              .slice(0, 6)
              .map((w) => ({ icon: 'stack', label: w.street, note: [w.name, w.day ? `for ${w.day === m.today ? 'today' : w.day}` : null].filter(Boolean).join(' · '), run: () => openWaiting(w.id) }))
          : [],
      ],
      ['Zones', ql ? m.zones.filter((z) => has(z.name)).map((z) => ({ icon: 'polygon', label: z.name, note: z.couriers.map((u) => m.person(u)?.first).filter(Boolean).join(', ') || 'No couriers yet', run: () => openZones(z.id) })) : []],
      ['Go to', PAGES.filter(([, , l]) => has(l)).map(([k, i, l]) => ({ icon: i, label: l, run: () => go(k) }))],
      [
        'Actions',
        [
          { icon: 'path', label: 'Plan a route', run: () => planWith() },
          { icon: 'plus', label: 'Add a stop to Waiting', run: () => openDialog({ kind: 'addWaiting' }) },
          { icon: 'table', label: 'Import a sheet', run: importSheet },
          { icon: 'userPlus', label: 'Invite a courier', run: () => openDialog({ kind: 'invite' }) },
          ...(m.waiting.length && m.people.length
            ? [
                {
                  icon: 'arrowsSplit',
                  // On the Waiting tab, the stops it shows (its filters); elsewhere, every stop waiting.
                  label: `Share out the stops waiting · ${ui.get().page === 'stops' && ui.get().tab === 'wait' ? waitingRows(m, ui.get()).length : m.waiting.length}`,
                  run: () => openShare((ui.get().page === 'stops' && ui.get().tab === 'wait' ? waitingRows(m, ui.get()) : m.waiting).map((w) => w.id), m.people.map((p) => p.uid)),
                },
              ]
            : []),
          { icon: 'polygon', label: 'Draw a zone', run: () => drawZone() },
          { icon: 'polygon', label: `Zones${m.zones.length ? ` · ${m.zones.length}` : ''}`, run: () => openZones() },
          { icon: 'stack', label: `Stops waiting for a route${m.waiting.length ? ` · ${m.waiting.length}` : ''}`, run: () => openWaiting() },
          { icon: 'mapTrifold', label: ui.get().map ? 'Show only the list' : 'Show the map', run: () => setMap(!ui.get().map) },
          { icon: ui.get().theme === 'dark' ? 'sun' : 'moon', label: ui.get().theme === 'dark' ? 'Switch to light' : 'Switch to dark', run: theme },
        ].filter((a) => has(a.label)),
      ],
      [
        past.total > past.hits.length ? `Past days · the newest ${past.hits.length} of ${past.total}` : 'Past days',
        past.hits.map(({ r, stop, day }) => ({
          icon: 'clock',
          label: streetOf(stop),
          note: [dayWords(day, { short: true }), routeTitle(r, m.zone), m.person(r.riderId)?.first].filter(Boolean).join(' · '),
          run: () => {
            go('routes');
            ui.set({ rday: day });
            openRoute(r.id);
            track('search_past_opened', { found: past.total });
          },
        })),
      ],
    ];
    return out.filter(([g, xs]) => g && xs.length);
  }, [open, q, m]);
  const hits = groups.flatMap(([, xs]) => xs);
  useEffect(() => {
    res.current?.querySelector('.act')?.scrollIntoView({ block: 'nearest' });
  }, [sel]);
  if (!open) return null;
  const run = (h: Hit) => {
    toggleCmdk(false);
    h.run();
  };
  let k = -1;
  return (
    <div id="cmdk" className="on" onClick={(e) => e.target === e.currentTarget && toggleCmdk(false)}>
      <div className="box" role="dialog" aria-label="Search">
        <input
          ref={input}
          id="cmdkq"
          placeholder="Search couriers, stops, customers, or type a command"
          autoComplete="off"
          spellCheck={false}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setSel(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              setSel((x) => (x + 1) % Math.max(hits.length, 1));
              e.preventDefault();
            } else if (e.key === 'ArrowUp') {
              setSel((x) => (x - 1 + hits.length) % Math.max(hits.length, 1));
              e.preventDefault();
            } else if (e.key === 'Enter' && hits[sel]) run(hits[sel]);
            else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              toggleCmdk(false);
            }
          }}
        />
        <div className="res" ref={res}>
          {hits.length ? (
            groups.map(([g, xs]) => (
              <div key={g}>
                <div className="grp eyebrow">{g}</div>
                {xs.map((h) => {
                  k++;
                  const i = k;
                  return (
                    <div key={`${g}:${h.label}:${i}`} className={`mi${i === sel ? ' act' : ''}`} onMouseMove={() => i !== sel && setSel(i)} onClick={() => run(h)}>
                      {h.avatar ? <Av p={{ hex: h.avatar.hex, on: h.avatar.on, first: h.avatar.first }} size="sm" /> : <Icon name={h.icon ?? 'dotsThree'} />}
                      <b className="grow ellipsis">{h.label}</b>
                      {h.note && <span className="cap ellipsis">{h.note}</span>}
                      {h.key && <span className="kbd">{h.key}</span>}
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            <EmptyHelp
              q={q.trim()}
              tries={[
                ...nearly(q, m.stops, (s) => `${s.address} ${s.name ?? ''}`).map((s) => ({ icon: 'mapPin', label: streetOf(s), note: [s.name, m.person(s.riderId)?.first].filter(Boolean).join(' · '), act: `stop:${keyOf(s)}` })),
                { icon: 'users', label: 'A courier’s name', note: and(m.people.map((p) => p.first)), act: 'clear' },
                { icon: 'mapPin', label: 'A street, a customer or a phone number', act: 'clear' },
              ]}
              onTry={(a) => {
                if (a.startsWith('stop:')) {
                  toggleCmdk(false);
                  openStop(a.slice(5));
                } else {
                  setQ('');
                  input.current?.focus();
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
